/**
 * 浏览器打印：从预览 DOM 克隆内容，清除缩放/裁切等仅用于屏幕预览的样式。
 * 避免「模板预览清晰、正式打印发糊或字母显示不全」。
 */

function clearInlineBox (el) {
  if (!el?.style) return
  el.style.transform = ''
  el.style.zoom = ''
  el.style.willChange = ''
}

/** 清除单页缩放、页眉 zoom、表格单元格 overflow 裁切等 */
export function normalizePrintCloneNode (root) {
  if (!root) return
  clearInlineBox(root)

  const shellSel = [
    '.print-page__content-shell',
    '.print-page__content',
    '.print-page',
    '.print-table-region',
    '.print-table',
    '.print-body'
  ].join(',')

  root.querySelectorAll(shellSel).forEach((el) => {
    if (!el.style) return
    el.style.transform = ''
    el.style.zoom = ''
    el.style.willChange = ''
    el.style.overflow = 'visible'
    el.style.height = ''
    el.style.minHeight = ''
    el.style.maxHeight = ''
    if (el.classList?.contains('print-page__content')) {
      el.style.width = '100%'
    }
  })

  root.querySelectorAll('.print-header').forEach((el) => {
    if (!el.style) return
    el.style.zoom = ''
    el.style.transform = ''
    el.style.overflow = 'visible'
  })

  root.querySelectorAll('table tr, table th, table td').forEach((el) => {
    if (!el.style) return
    el.style.overflow = 'visible'
    el.style.textOverflow = ''
    el.style.maxHeight = ''
    if (el.tagName === 'TR') {
      el.style.height = ''
    }
  })
}

/**
 * @param {HTMLElement} previewContentEl PrintPreview 内 .preview-content 根节点
 * @returns {string} 可写入打印窗口的 HTML
 */
export function cloneStandardPrintContentHtml (previewContentEl) {
  if (!previewContentEl) return ''
  const wrap = document.createElement('div')
  wrap.innerHTML = previewContentEl.innerHTML
  normalizePrintCloneNode(wrap)
  return wrap.innerHTML
}

/** 送货单/派工单等实体打印增强 CSS（写入打印窗口 <style>） */
export function buildPhysicalPrinterEnhancementCss ({
  printBodyFs = 15,
  printBodyFf = 'inherit',
  isDeliveryLike = false
} = {}) {
  if (!isDeliveryLike) return ''
  const fs = Math.max(Number(printBodyFs) || 15, 15)
  return `
    html, body {
      font-family: ${printBodyFf};
      font-size: ${fs}px;
      color: #000 !important;
      -webkit-font-smoothing: auto !important;
      text-rendering: auto !important;
    }
    @media print {
      .print-hint { display: none !important; }
      html, body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        -webkit-text-size-adjust: 100% !important;
        text-size-adjust: 100% !important;
      }
      * {
        -webkit-text-size-adjust: 100% !important;
        text-size-adjust: 100% !important;
      }
      .print-page__content-shell,
      .print-page__content,
      .print-header,
      .print-body,
      .print-table-region,
      .print-table {
        transform: none !important;
        zoom: 1 !important;
        overflow: visible !important;
        height: auto !important;
        max-height: none !important;
      }
      .print-table table {
        width: 100% !important;
        table-layout: fixed !important;
        border-collapse: collapse !important;
      }
      .print-table th,
      .print-table td {
        overflow: visible !important;
        text-overflow: clip !important;
        height: auto !important;
        min-height: 1.2em !important;
        max-height: none !important;
        line-height: 1.35 !important;
        vertical-align: top !important;
        border-width: 1px !important;
        border-style: solid !important;
        border-color: #000 !important;
        box-sizing: border-box !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      .print-table tbody tr {
        height: auto !important;
        max-height: none !important;
        page-break-inside: avoid;
      }
      .print-table thead th {
        font-weight: 700 !important;
        background: #fff !important;
      }
      .print-table thead th.print-table-header--merged {
        text-align: center !important;
        vertical-align: middle !important;
      }
      .basic-fields-section,
      .basic-field-item,
      .print-notes,
      .print-signatures {
        overflow: visible !important;
        line-height: 1.35 !important;
      }
    }
  `
}

export function buildPrintWindowHintHtml () {
  return `<p class="print-hint" style="margin:8px 12px;font-size:12px;color:#666;font-family:system-ui,sans-serif;line-height:1.5;">
  <strong>360 安全浏览器：</strong>请用<strong>极速模式</strong>；打印对话框缩放选「<strong>100%</strong>」。若勾选「<strong>按系统模板打印</strong>」，系统对话框往往<strong>没有预览</strong>，请以本窗口上方内容为准核对后再点「确认打印」。
</p>`
}

/** 打印预览窗口顶栏：屏幕可见，@media print 时隐藏 */
export function buildPrintPreviewToolbarHtml () {
  return `<div class="erp-print-toolbar" id="erp-print-toolbar">
  <div class="erp-print-toolbar__inner">
    <strong>打印预览</strong>
    <span class="erp-print-toolbar__hint">请核对下方内容与纸张方向；360 勾选「按系统模板打印」时系统对话框可能无预览，以本页为准。</span>
    <button type="button" class="erp-print-toolbar__btn erp-print-toolbar__btn--primary" id="erp-print-confirm">确认打印</button>
    <button type="button" class="erp-print-toolbar__btn" onclick="window.close()">关闭</button>
  </div>
</div>
<script>
(function () {
  var btn = document.getElementById('erp-print-confirm');
  if (!btn) return;
  btn.addEventListener('click', function () {
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () { window.print(); }).catch(function () { window.print(); });
    } else {
      window.print();
    }
  });
})();
</script>`
}

export const PRINT_PREVIEW_TOOLBAR_CSS = `
.erp-print-toolbar {
  position: sticky;
  top: 0;
  z-index: 9999;
  background: linear-gradient(180deg, #ecf5ff 0%, #f5f7fa 100%);
  border-bottom: 1px solid #dcdfe6;
  padding: 10px 16px;
  font-family: system-ui, "Microsoft YaHei", sans-serif;
  font-size: 13px;
  color: #303133;
  box-shadow: 0 2px 8px rgba(0,0,0,.06);
}
.erp-print-toolbar__inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px 16px;
  max-width: 1200px;
  margin: 0 auto;
}
.erp-print-toolbar__hint {
  flex: 1 1 280px;
  color: #606266;
  line-height: 1.45;
  font-size: 12px;
}
.erp-print-toolbar__btn {
  padding: 6px 16px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  font-size: 13px;
}
.erp-print-toolbar__btn--primary {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}
.erp-print-toolbar__btn:hover { opacity: 0.92; }
.erp-print-doc { padding: 8px 0 24px; }
@media print {
  .erp-print-toolbar, .print-hint { display: none !important; }
  .erp-print-doc { padding: 0 !important; }
}
`

/** 360/Chrome 等：等字体就绪后再调 print，减少缺字、发糊 */
export async function waitForPrintWindowReady (printWindow, { extraMs = 480 } = {}) {
  try {
    if (printWindow?.document?.fonts?.ready) {
      await printWindow.document.fonts.ready
    }
  } catch {
    /* ignore */
  }
  await new Promise((r) => setTimeout(r, Math.max(200, Number(extraMs) || 480)))
}

export function triggerPrintWindow (printWindow, { onDone, extraMs = 480 } = {}) {
  if (!printWindow) return
  printWindow.focus()
  waitForPrintWindowReady(printWindow, { extraMs }).then(() => {
    try {
      printWindow.print()
      onDone?.()
    } catch {
      /* user cancelled or popup blocked */
    }
  })
}
